import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createTeleporter } from 'react-teleporter';

import { Hidden, AppBar } from '@mui/material';

import withStyles from '@mui/styles/withStyles';

import { translate } from '@openclassrooms/translations';

import { styled } from '@classify/react-core-components/styles';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';

import { PredictiveCRMTopBanner } from 'Modules/predictive-crm/components';

import HeaderNavigationDropDown from '../../components/HeaderNavigationDropDown';
import TrainingDropDownMenu from './TrainingDropDownMenu';
import ApprenticeshipDropDownMenu from './ApprenticeshipDropDownMenu';
import MainHeaderNavigationLinkWrapper from '../../components/MainHeaderNavigationLinkWrapper';
import MainHeaderDrawer from '../../components/MainHeaderDrawer';
import MainHeaderActions from '../../components/MainHeaderActions';
import MainHeaderLogo from '../../components/MainHeaderLogo';
import HeaderSeparator from '../../components/HeaderSeparator';

import mainNavigation from '../../services/mainNavigation';
import styles from './styles';
import BusinessDropDownMenu from './BusinessDropDownMenu';
import useHeaderConfiguration from '../../hooks/useHeaderConfiguration';
import SearchButton from './SearchButton';

interface User {
  id?: number;
  displayableName?: string;
  profilePicture?: string;
  organizationName?: string;
  link?: {
    profile?: string;
    logOut?: string;
    employerSpace?: string;
    parameter?: string;
  };
}

interface Right {
  canSeeMentorDashboard?: boolean;
  canSeeAdminDashboard?: boolean;
  canSeeTutorDashboard?: boolean;
  canSeeOrganizationSpace?: boolean;
}

interface Props {
  classes?: Record<string, string>;
  isLegacy?: boolean;
  hasRouter?: boolean;
  isConnected?: boolean;
  isNavigationHidden?: boolean;
  isNavigationCollapsed?: boolean;
  isWide?: boolean;
  additionalMenu?: ReactNode;
  user?: User & {
    notificationCount: number;
    moderationNotificationCount: number;
    messageCount: number;
    profileCompletionRate?: number | null;
  };
  right?: Right;
  onLogOut?: () => void;
}

const TeleporterWrapper = styled('div')(() => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}));

const TeleporterInnerWrapper = styled('div')(({ theme: { breakpoints } }) => ({
  height: 44,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  '&:last-of-type': {
    transition: 'opacity 0.2s ease-in-out 0.2s, max-width 0.2s ease-in-out',
    flexGrow: 1,
    [breakpoints.up('sm')]: {
      maxWidth: '100%',
    },
    '& > a:last-of-type': {
      transition: 'all 0.2s ease-in-out',
    },
  },
  '&:not(:last-of-type)': {
    transition: 'opacity 0.2s ease-in-out, max-width 0.2s ease-in-out 0.2s',
    flexGrow: 0,
    maxWidth: 0,
    overflow: 'hidden',
    opacity: 0,
    '& > a:last-of-type': {
      opacity: 0,
      transition: 'none',
    },
  },
}));

export const HeaderSearchTeleporter = createTeleporter();

function MainHeader({
  classes = {},
  isConnected,
  user = {
    notificationCount: 0,
    moderationNotificationCount: 0,
    messageCount: 0,
    profileCompletionRate: null,
  },
  right = {},
  isLegacy,
  isWide,
  additionalMenu = null,
  hasRouter,
  isNavigationHidden,
  onLogOut = () => {},
}: Props) {
  const headerConfig = useHeaderConfiguration();

  const [isTrainingMenuOpen, toggleTrainingMenu] = useState(false);
  const [isApprenticeshipMenuOpen, toggleApprenticeshipMenu] = useState(false);
  const [isBusinessMenuOpen, toggleBusinessMenu] = useState(false);

  const headerRef = useRef(null);

  const navigationConfig = mainNavigation();
  const InnerWrapper = isWide ? 'div' : ContentWrapper;

  return (
    <div className={classes.root} ref={headerRef}>
      <PredictiveCRMTopBanner />
      <AppBar
        classes={{ root: classes.header }}
        elevation={0}
        position="static"
        id="header"
        role="banner"
      >
        <InnerWrapper className={isWide ? classes.innerWrapper : classes.contentWrapper}>
          <div className={classes.content}>
            <Hidden mdUp>
              <MainHeaderDrawer
                isLegacy={isLegacy}
                hasRouter={hasRouter}
                isConnected={isConnected}
                additionalMenu={additionalMenu}
                shouldShowLoginButton
              />
            </Hidden>

            <MainHeaderLogo isLegacy={isLegacy} />

            <HeaderSearchTeleporter.Target as={TeleporterWrapper}>
              <TeleporterInnerWrapper>
                {!isNavigationHidden && (
                  <Hidden mdDown>
                    <nav
                      id="main-menu-navigation"
                      className={classes.navWrapper}
                      role="navigation"
                      aria-label={translate('main_header.main_menu')}
                      data-testid="mainHeaderNavigation"
                    >
                      <div className={classes.linksWrapper}>
                        <HeaderNavigationDropDown
                          name="training"
                          anchorEl={headerRef}
                          isOpen={isTrainingMenuOpen}
                          link={navigationConfig.TRAINING}
                          toggleMenu={toggleTrainingMenu}
                          menuComponent={TrainingDropDownMenu}
                        />

                        {navigationConfig.APPRENTICESHIP.shouldBeDisplayed && (
                          <HeaderNavigationDropDown
                            name="apprenticeship"
                            anchorEl={headerRef}
                            isOpen={isApprenticeshipMenuOpen}
                            link={navigationConfig.APPRENTICESHIP}
                            toggleMenu={toggleApprenticeshipMenu}
                            menuComponent={ApprenticeshipDropDownMenu}
                          />
                        )}

                        {navigationConfig.FUNDING.shouldBeDisplayed && (
                          <MainHeaderNavigationLinkWrapper
                            isLegacy={isLegacy}
                            {...navigationConfig.FUNDING}
                          />
                        )}

                        <div className={classes.divider}>
                          <HeaderSeparator />
                        </div>
                        <HeaderNavigationDropDown
                          name="business"
                          anchorEl={headerRef}
                          isOpen={isBusinessMenuOpen}
                          link={navigationConfig.BUSINESS}
                          toggleMenu={toggleBusinessMenu}
                          menuComponent={BusinessDropDownMenu}
                        />
                      </div>
                    </nav>
                  </Hidden>
                )}
                <SearchButton />
              </TeleporterInnerWrapper>
            </HeaderSearchTeleporter.Target>

            <MainHeaderActions
              user={
                headerConfig.isConnected
                  ? {
                      ...headerConfig.user,
                      notificationCount: user.notificationCount,
                      moderationNotificationCount: user.moderationNotificationCount,
                      messageCount: user.messageCount,
                    }
                  : null
              }
              right={right}
              onLogOut={onLogOut}
            />
          </div>
        </InnerWrapper>
      </AppBar>
    </div>
  );
}

export default withStyles(styles)(MainHeader);
